/**
 * Cloudinary configuration and utilities
 * 
 * To use Cloudinary CDN:
 * 1. Sign up at https://cloudinary.com
 * 2. Add your cloud name to .env as VITE_CLOUDINARY_CLOUD_NAME
 * 3. Upload images to your Cloudinary media library
 */

import { Cloudinary } from '@cloudinary/url-gen';
import { quality, format } from '@cloudinary/url-gen/actions/delivery';
import { fill, scale } from '@cloudinary/url-gen/actions/resize';

// Initialize Cloudinary instance
export const cld = new Cloudinary({
  cloud: {
    cloudName: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || 'demo'
  }
});

/**
 * Generate responsive image URLs with Cloudinary transformations
 */
export const generateResponsiveUrls = (
  publicId: string,
  widths: number[] = [320, 640, 768, 1024, 1280, 1920]
) => {
  return widths.map(width => {
    const image = cld.image(publicId)
      .delivery(format('auto'))
      .delivery(quality('auto'))
      .resize(scale().width(width));
    
    return {
      width,
      url: image.toURL()
    };
  });
};

/**
 * Generate srcset string for responsive images
 */
export const generateSrcSet = (publicId: string, widths?: number[]): string => {
  const urls = generateResponsiveUrls(publicId, widths);
  return urls.map(({ url, width }) => `${url} ${width}w`).join(', ');
};

/**
 * Generate optimized image URL
 */
export const getOptimizedImageUrl = (
  publicId: string,
  width?: number,
  height?: number
): string => {
  let image = cld.image(publicId)
    .delivery(format('auto'))
    .delivery(quality('auto'));
  
  if (width && height) {
    image = image.resize(fill().width(width).height(height));
  } else if (width) {
    image = image.resize(scale().width(width));
  } else if (height) {
    image = image.resize(scale().height(height));
  }
  
  return image.toURL();
};

/**
 * Check if Cloudinary is configured
 */
export const isCloudinaryConfigured = (): boolean => {
  return Boolean(import.meta.env.VITE_CLOUDINARY_CLOUD_NAME && 
                 import.meta.env.VITE_CLOUDINARY_CLOUD_NAME !== 'demo');
};
